import Swal from 'sweetalert2';
import type { Contest } from '@/mocks/adminContests';
import { contestTypeLabels, contestTypeIcons, contestTypeColors, contestStatusColors, contestStatusLabels } from '@/mocks/adminContests';

interface ContestCardProps {
  contest: Contest;
  onEdit: (contest: Contest) => void;
  onDelete: (contest: Contest) => void;
  onViewRules: (contest: Contest) => void;
  onViewWinners: (contest: Contest) => void;
  onViewParticipants: (contest: Contest) => void;
  onActivate: (contest: Contest) => void;
  onPause: (contest: Contest) => void;
  onFinalize: (contest: Contest) => void;
  onPickWinner: (contest: Contest) => void;
  onShare: (contest: Contest) => void;
  onPublish: (contest: Contest) => void;
  onPublishWinner: (contest: Contest) => void;
  onExportParticipants: (contest: Contest) => void;
}

const sharedSwal = Swal.mixin({
  customClass: {
    popup: 'rounded-2xl',
    title: 'font-bold',
    confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
    cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
    htmlContainer: 'text-sm',
  },
  buttonsStyling: false,
  showConfirmButton: false,
  showCloseButton: true,
});

function showActionsModal(
  contest: Contest,
  handlers: {
    onEdit: (c: Contest) => void;
    onDelete: (c: Contest) => void;
    onViewRules: (c: Contest) => void;
    onViewWinners: (c: Contest) => void;
    onViewParticipants: (c: Contest) => void;
    onActivate: (c: Contest) => void;
    onPause: (c: Contest) => void;
    onFinalize: (c: Contest) => void;
    onPickWinner: (c: Contest) => void;
    onShare: (c: Contest) => void;
    onPublish: (c: Contest) => void;
    onPublishWinner: (c: Contest) => void;
    onExportParticipants: (c: Contest) => void;
  }
) {
  const actionBtn = (icon: string, label: string, color: string, hoverBg: string) =>
    `<button type="button" data-action="${label.toLowerCase().replace(/\s/g, '-')}"
      class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-left transition-colors cursor-pointer ${color} ${hoverBg}">
      <span class="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style="background:currentColor;opacity:0.1">
        <i class="${icon} text-base" style="color:currentColor"></i>
      </span>
      ${label}
    </button>`;

  const actions: string[] = [];

  actions.push(actionBtn('ri-edit-line', 'Editar', 'text-foreground-700', 'hover:bg-background-100'));
  actions.push(actionBtn('ri-file-text-line', 'Ver reglas', 'text-foreground-700', 'hover:bg-background-100'));
  actions.push(actionBtn('ri-group-line', 'Ver participantes', 'text-foreground-700', 'hover:bg-background-100'));

  if (contest.participants > 0) {
    actions.push(actionBtn('ri-file-download-line', 'Exportar participantes', 'text-emerald-600', 'hover:bg-emerald-50'));
  }

  if (contest.status === 'finalizado' && contest.winnerName) {
    actions.push(actionBtn('ri-trophy-line', 'Ver ganador', 'text-amber-600', 'hover:bg-amber-50'));
  }

  actions.push('<div class="border-t border-background-200 my-1"></div>');

  if (contest.status !== 'finalizado') {
    actions.push(actionBtn('ri-share-forward-line', 'Compartir', 'text-sky-600', 'hover:bg-sky-50'));
  }

  if (!contest.published && contest.status !== 'finalizado') {
    actions.push(actionBtn('ri-send-plane-line', 'Publicar', 'text-emerald-600', 'hover:bg-emerald-50'));
  }

  if (contest.status === 'finalizado' && contest.winnerName && !contest.winnerPublished) {
    actions.push(actionBtn('ri-megaphone-line', 'Publicar ganador', 'text-amber-600', 'hover:bg-amber-50'));
  }

  actions.push('<div class="border-t border-background-200 my-1"></div>');

  if (contest.status === 'activo') {
    actions.push(actionBtn('ri-pause-circle-line', 'Pausar', 'text-orange-600', 'hover:bg-orange-50'));
  }
  if (contest.status === 'pausado') {
    actions.push(actionBtn('ri-play-circle-line', 'Activar', 'text-emerald-600', 'hover:bg-emerald-50'));
  }
  if (contest.status === 'activo' || contest.status === 'pausado') {
    actions.push(actionBtn('ri-stop-circle-line', 'Finalizar', 'text-foreground-600', 'hover:bg-foreground-100'));
  }
  if (contest.status !== 'finalizado') {
    actions.push(actionBtn('ri-trophy-line', 'Elegir ganador', 'text-amber-600', 'hover:bg-amber-50'));
  }

  actions.push('<div class="border-t border-background-200 my-1"></div>');
  actions.push(actionBtn('ri-delete-bin-line', 'Eliminar', 'text-red-600', 'hover:bg-red-50'));

  sharedSwal
    .fire({
      title: contest.title,
      html: `
        <div class="text-left space-y-1">
          <p class="text-xs text-foreground-500 mb-2 line-clamp-2">${escapeHtml(contest.description)}</p>
          <div class="flex items-center gap-2 mb-3 flex-wrap">
            <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${contestTypeColors[contest.type].bg} ${contestTypeColors[contest.type].text} border ${contestTypeColors[contest.type].border}">
              <i class="${contestTypeIcons[contest.type]} text-[9px]"></i>${contestTypeLabels[contest.type]}
            </span>
            <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${contestStatusColors[contest.status].bg} ${contestStatusColors[contest.status].text}">
              <span class="w-1.5 h-1.5 rounded-full ${contestStatusColors[contest.status].dot}"></span>${contestStatusLabels[contest.status]}
            </span>
          </div>
          <div class="flex items-center gap-2 mb-3 px-3 py-2 rounded-xl bg-amber-50 border border-amber-100">
            <div class="w-7 h-7 rounded-lg bg-amber-200 flex items-center justify-center shrink-0">
              <i class="ri-gift-line text-amber-700 text-xs"></i>
            </div>
            <div class="min-w-0">
              <p class="text-amber-700 text-[10px] font-semibold uppercase">Premio</p>
              <p class="text-amber-900 text-xs font-bold truncate">${escapeHtml(contest.prize)}</p>
            </div>
          </div>
          ${actions.join('')}
        </div>
      `,
      customClass: {
        popup: 'rounded-2xl max-w-sm',
        title: 'font-bold text-sm',
        htmlContainer: '!p-0',
      },
      didOpen: () => {
        const popup = Swal.getPopup();
        if (!popup) return;

        const map: Record<string, () => void> = {
          'editar': () => handlers.onEdit(contest),
          'ver-reglas': () => handlers.onViewRules(contest),
          'ver-participantes': () => handlers.onViewParticipants(contest),
          'ver-ganador': () => handlers.onViewWinners(contest),
          'compartir': () => handlers.onShare(contest),
          'publicar': () => handlers.onPublish(contest),
          'publicar-ganador': () => handlers.onPublishWinner(contest),
          'exportar-participantes': () => handlers.onExportParticipants(contest),
          'pausar': () => handlers.onPause(contest),
          'activar': () => handlers.onActivate(contest),
          'finalizar': () => handlers.onFinalize(contest),
          'elegir-ganador': () => handlers.onPickWinner(contest),
          'eliminar': () => handlers.onDelete(contest),
        };

        Object.entries(map).forEach(([action, fn]) => {
          popup.querySelector(`[data-action="${action}"]`)?.addEventListener('click', () => {
            Swal.close();
            fn();
          });
        });
      },
    });
}

export default function ContestCard({
  contest,
  onEdit,
  onDelete,
  onViewRules,
  onViewWinners,
  onViewParticipants,
  onActivate,
  onPause,
  onFinalize,
  onPickWinner,
  onShare,
  onPublish,
  onPublishWinner,
  onExportParticipants,
}: ContestCardProps) {
  const typeStyle = contestTypeColors[contest.type];
  const statusStyle = contestStatusColors[contest.status];
  const now = new Date();
  const startDate = new Date(contest.startDate);
  const endDate = new Date(contest.endDate);
  const daysLeft = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  return (
    <div className="rounded-2xl border border-background-200/70 bg-background-50 overflow-hidden transition-all hover:border-background-300/80 group">
      {/* Type-colored top bar */}
      <div className={`h-2 ${typeStyle.bar}`}></div>

      <div className="p-4 md:p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${typeStyle.bg} ${typeStyle.text} border ${typeStyle.border}`}>
                <i className={`${contestTypeIcons[contest.type]} text-[10px]`}></i>
                {contestTypeLabels[contest.type]}
              </span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${statusStyle.bg} ${statusStyle.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`}></span>
                {contestStatusLabels[contest.status]}
              </span>
              {!contest.published && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-foreground-100 text-foreground-500">
                  <i className="ri-draft-line text-[10px]"></i>
                  Borrador
                </span>
              )}
              {contest.status === 'finalizado' && contest.winnerPublished && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                  <i className="ri-megaphone-fill text-[10px]"></i>
                  Ganador público
                </span>
              )}
            </div>
            <h3 className="text-foreground-950 font-bold text-sm leading-tight">{contest.title}</h3>
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              showActionsModal(contest, {
                onEdit, onDelete, onViewRules, onViewWinners, onViewParticipants,
                onActivate, onPause, onFinalize, onPickWinner, onShare, onPublish, onPublishWinner, onExportParticipants,
              });
            }}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors opacity-0 group-hover:opacity-100 shrink-0"
            title="Acciones"
          >
            <i className="ri-more-2-fill text-foreground-500 text-sm"></i>
          </button>
        </div>

        <p className="text-foreground-500 text-xs leading-relaxed mb-3 line-clamp-2">{contest.description}</p>

        {/* Prize */}
        <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-xl bg-amber-50 border border-amber-100">
          <div className="w-7 h-7 rounded-lg bg-amber-200 flex items-center justify-center shrink-0">
            <i className="ri-gift-line text-amber-700 text-xs"></i>
          </div>
          <div className="min-w-0">
            <p className="text-amber-700 text-[10px] font-semibold uppercase tracking-wide">Premio</p>
            <p className="text-amber-900 text-xs font-bold truncate">{contest.prize}</p>
          </div>
        </div>

        {/* Stats row */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="text-foreground-400 text-[11px] flex items-center gap-1">
              <i className="ri-calendar-line text-[10px]"></i>
              {startDate.toLocaleDateString('es-MX', { month: 'short', day: 'numeric' })} - {endDate.toLocaleDateString('es-MX', { month: 'short', day: 'numeric' })}
            </span>
            <span className="text-foreground-400 text-[11px] flex items-center gap-1">
              <i className="ri-user-line text-[10px]"></i>
              {contest.participants.toLocaleString()}
            </span>
            <span className="text-foreground-400 text-[11px] flex items-center gap-1">
              <i className="ri-share-forward-line text-[10px]"></i>
              {contest.shares.toLocaleString()}
            </span>
          </div>

          {contest.status === 'activo' && daysLeft > 0 && (
            <span className="text-emerald-600 text-[11px] font-bold flex items-center gap-1">
              <i className="ri-time-line text-[10px]"></i>
              {daysLeft} día{daysLeft !== 1 ? 's' : ''}
            </span>
          )}

          {contest.status === 'pausado' && (
            <span className="text-orange-600 text-[11px] font-bold flex items-center gap-1">
              <i className="ri-pause-circle-fill text-[10px]"></i>
              En pausa
            </span>
          )}

          {contest.status === 'proximamente' && (
            <span className="text-amber-600 text-[11px] font-bold flex items-center gap-1">
              <i className="ri-time-line text-[10px]"></i>
              Empieza en {daysLeft} día{daysLeft !== 1 ? 's' : ''}
            </span>
          )}

          {contest.status === 'finalizado' && contest.winnerName && (
            <span className="text-amber-600 text-[11px] font-bold flex items-center gap-1">
              <i className="ri-trophy-fill text-[10px]"></i>
              {contest.winnerName.split(' ')[0]}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}